// Check campaign name
import Cookies from 'js-cookie'
import imageCfUrl from '@/config/imageCfUrl'

const listHide = ['pre-order-iphone-15-series']

// Member storage is transitioning from localStorage-only to cookie-based
// storage. New writes go to cookies (~2 months); pre-existing localStorage
// entries are left in place and used as a read fallback until a fresh
// cookie write replaces them.
const MEMBER_COOKIE_OPTIONS = {
  expires: 60, // ~2 months
  sameSite: 'Lax',
  secure: true,
}

// Legacy flat localStorage keys (one per data type). Kept only to generate
// the fallback `legacyKey` argument passed into the helpers below — the
// cookie itself now consolidates all of a slug's fields into one object.
export const getRegisteredStorageKey = slug =>
  slug ? `registered_${slug}` : 'registered'

export const getMemberVerifiedStorageKey = slug =>
  slug ? `member_verified_${slug}` : 'member_verified'

export const getMemberIdVerifiedStorageKey = slug =>
  slug ? `member_id_verified_${slug}` : 'member_id_verified'

export const getRegisteredOrderStorageKey = slug =>
  slug ? `registered_order_${slug}` : 'registered_order'

// New cookie storage: one cookie per slug, e.g. member_ip99, holding a JSON
// object of fields ({ registered, phone, member_id, registered_order }).
const getMemberStorageKey = slug =>
  slug ? `member_${slug}` : 'member'

const readMemberCookie = slug => {
  try {
    const parsed = JSON.parse(
      Cookies.get(getMemberStorageKey(slug)) || '{}',
    )
    return parsed &&
      typeof parsed === 'object' &&
      !Array.isArray(parsed)
      ? parsed
      : {}
  } catch {
    return {}
  }
}

const writeMemberCookie = (slug, data) => {
  if (Object.keys(data).length === 0) {
    Cookies.remove(getMemberStorageKey(slug))
    return
  }
  Cookies.set(
    getMemberStorageKey(slug),
    JSON.stringify(data),
    MEMBER_COOKIE_OPTIONS,
  )
}

// Reads one field from the per-slug member cookie, falling back to the
// corresponding legacy flat localStorage key if the cookie/field is absent.
export const getMemberStorageItem = (slug, field, legacyKey) =>
  readMemberCookie(slug)[field] ??
  (legacyKey ? localStorage.getItem(legacyKey) : undefined)

// Writes one field into the consolidated per-slug member cookie.
export const setMemberStorageItem = (slug, field, value) =>
  writeMemberCookie(slug, {
    ...readMemberCookie(slug),
    [field]: value,
  })

// Removes one field from the cookie (deleting the cookie entirely once it
// has no fields left) and clears the corresponding legacy localStorage key.
export const removeMemberStorageItem = (slug, field, legacyKey) => {
  const current = readMemberCookie(slug)
  delete current[field]
  writeMemberCookie(slug, current)
  if (legacyKey) localStorage.removeItem(legacyKey)
}

export const getVerifiedMemberId = slug =>
  getMemberStorageItem(
    slug,
    'member_id',
    getMemberIdVerifiedStorageKey(slug),
  ) || ''

export const isShowDelivery = campaignSlug => {
  // check contain iPhone 14,iPhone 15
  if (listHide.includes(campaignSlug.toLowerCase())) {
    return false
  }
  // because selected from checkout is delivery or pickup in store
  return true
}

// Only images served from these domains are routed through the Cloudflare image
// resizer. A leading `*.` matches the apex domain and any subdomain.
const RESIZE_IMAGE_DOMAINS = [
  '*.bnn.com7.in',
  '*.studio7.com7.in',
  '*.bnn.in.th',
  '*.studio7thailand.com',
]

const matchesResizeDomain = hostname =>
  RESIZE_IMAGE_DOMAINS.some(pattern => {
    const base = pattern.replace(/^\*\./, '')
    return hostname === base || hostname.endsWith(`.${base}`)
  })

export const resizeImage = (url, width = 1000) => {
  if (typeof url !== 'string' || !url) {
    return url
  }

  let hostname
  try {
    hostname = new URL(url).hostname
  } catch {
    return url
  }

  return matchesResizeDomain(hostname)
    ? `${imageCfUrl}width=${width}/${url}`
    : url
}

/**
 * Validate thai id
 * @param {String} idCard
 * @returns {Boolean}
 */
export const validateIdCard = idCard => {
  if (
    !idCard.match(/^[0-9]{1}-[0-9]{4}-[0-9]{5}-[0-9]{2}-[0-9]{1}$/)
  ) {
    return false
  }

  idCard = idCard.replace(/-/g, '')

  if (typeof idCard !== 'string') {
    return false
  }
  const m = idCard.match(/(\d{12})(\d)/)
  if (!m) {
    return false
  }

  const digits = m[1].split('')
  const sum = digits.reduce((total, digit, i) => {
    return total + (13 - i) * +digit
  }, 0)
  const lastDigit = `${(11 - (sum % 11)) % 10}`
  const inputLastDigit = m[2]

  if (lastDigit !== inputLastDigit) {
    return false
  }

  return true
}

/**
 * Validate passport
 * @param {String} text
 * @returns {Boolean}
 */
export const validatePassport = text => {
  if (typeof text !== 'string') {
    return false
  }

  const regex = /^[A-Za-z0-9]{6,12}$/

  if (!regex.test(text)) {
    return false
  }
  return true
}

/**
 * Validate start with 0
 * @param {String} value
 * @returns {Boolean}
 */

export const validateStartWithZero = value => {
  const regex = /^0+/
  return regex.test(value)
}

/**
 * Check that a value is a well-formed absolute URL on the current domain
 * @param {String} value
 * @returns {Boolean}
 */
export const isSameOriginUrl = value => {
  try {
    return new URL(value).origin === window.location.origin
  } catch {
    return false
  }
}
